import React from "react";
import swal from "../../helpers/swal";
import ReactModal from "react-modal";
import is_money from "../../helpers/is_money";
import { axiosget, axiospost } from "../../helpers/httprequest";
import Swal from "sweetalert2";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { appHost } from "../../helpers/uri";
import { Button } from "../../components/ui/Button";

const ActionPayment = ({ v }) => {
  const [loadingVerify, setLoadingVerify] = React.useState(false);
  const [loadingInvoice, setLoadingInvoice] = React.useState(false);
  const [loadingWallet, setLoadingWallet] = React.useState(false);
  const [loadingReset, setLoadingReset] = React.useState(false);
  const [showmodal, setShowmodal] = React.useState(false);
  const queryClient = useQueryClient();

  // Fetch default payment gateway
  const { data: gatewayData } = useQuery({
    queryKey: ["paymentGateway"],
    queryFn: async () => {
      const req = await axiosget("ms_transactions/gateway");
      return req?.data;
    },
  });

  // Fetch wallet balance
  const { data: walletData } = useQuery({
    queryKey: ["walletBalance"],
    queryFn: async () => {
      const req = await axiosget("ms_student_wallet_transactions/balance");
      return req?.data;
    },
  });

  const gateway = gatewayData?.data;
  const walletBalance = walletData?.amount || 0;

  // Calculate total amount
  const totalAmount = v?.feeItems?.items?.reduce((a, b) => a + b?.amount, 0) || 0;
  const finalAmount = v?.feeItems?.scholar?.percentage
    ? totalAmount - (totalAmount / 100) * v?.feeItems?.scholar?.percentage
    : totalAmount;

  const _generateInvoice = async () => {
    try {
      setLoadingInvoice(true);

      // Use unified payment gateway API
      const response = await axiospost("ms_transactions/initialize", {
        amount: finalAmount,
        items: v?.feeItems?.items,
        level: v.level,
        semester: v.semester,
        serviceId: v?.feeItems?.fee?.serviceID,
      });

      setLoadingInvoice(false);

      if (!response?.data?.success) {
        swal("error", response?.data?.message || "Failed to initialize payment");
        return;
      }

      const paymentData = response.data.data;
      setShowmodal(false);

      // Show success with pay now/later options
      const result = await Swal.fire({
        icon: "success",
        title: paymentData.isExisting ? "Payment Reference Found!" : "Payment Initiated!",
        html: `
          <div style="text-align: center;">
            <p style="color: #666; margin-bottom: 16px;">
              Your payment has been ${paymentData.isExisting ? "retrieved" : "initiated"} via <strong>${paymentData.gatewayDisplayName || gateway?.displayName || paymentData.gateway}</strong>
            </p>
            ${paymentData.gatewayReference ? `
              <div style="background: #f0f9ff; border: 2px solid #bae6fd; border-radius: 8px; padding: 16px; margin-bottom: 16px;">
                <p style="font-size: 12px; font-weight: 600; color: #0369a1; margin-bottom: 8px;">Payment Reference</p>
                <p style="font-size: 18px; font-weight: bold; color: #0284c7; font-family: monospace;">${paymentData.gatewayReference}</p>
              </div>
            ` : ""}
            <p style="font-size: 14px; color: #888;">Click the button below to complete payment</p>
          </div>
        `,
        confirmButtonText: "Proceed to Payment",
        showCancelButton: true,
        cancelButtonText: "Pay Later",
        confirmButtonColor: "#4F46E5",
        cancelButtonColor: "#6B7280",
      });

      // Invalidate tuition history AFTER dialog closes - triggers refetch
      queryClient.invalidateQueries({ queryKey: ["myTuitionHistory"] });

      if (result.isConfirmed && paymentData.paymentUrl) {
        window.open(paymentData.paymentUrl, "_blank");
      }
    } catch (error) {
      swal("error", error?.message || "Payment initialization failed");
      setLoadingInvoice(false);
    }
  };

  const _payFromWallet = async () => {
    try {
      const newBalance = Number(walletBalance) - Number(finalAmount);
      const confirm = await Swal.fire({
        title: "CONFIRM WALLET PAYMENT",
        html: `Wallet Balance: <b>${is_money(walletBalance)}</b><br/>
        Invoice Amount: <b>${is_money(finalAmount)}</b><br/>
        New Wallet Balance: <b>${is_money(newBalance)}</b><br/>
        <div style="margin-top:10px;color:#0369a1;">Are you sure you want to pay <b>${is_money(finalAmount)}</b> from your wallet?</div>`,
        icon: "info",
        confirmButtonColor: "#4F46E5",
        showCancelButton: true,
        confirmButtonText: "Yes, Pay from Wallet",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;

      setLoadingWallet(true);
      const response = await axiospost("ms_transactions/wallet", {
        amount: finalAmount,
        items: v?.feeItems?.items,
        level: v.level,
        semester: v.semester,
        serviceId: v?.feeItems?.fee?.serviceID,
      });

      setLoadingWallet(false);
      const { type, message } = response?.data || {};
      swal(type || "error", message || "Something went wrong");
      if (type === "success") {
        queryClient.invalidateQueries({ queryKey: ["myTuitionHistory"] });
        queryClient.invalidateQueries({ queryKey: ["walletBalance"] });
        setShowmodal(false);
      }
    } catch (error) {
      setLoadingWallet(false);
      swal("error", error?.message || "Wallet payment failed");
    }
  };

  const _verifyPayment = async () => {
    try {
      setLoadingVerify(true);
      const reference = v?.attempted?.gatewayReference || v?.attempted?.rrr;
      const req = await axiosget(`ms_transactions/verify-gateway/${reference}`);
      swal(req?.data?.type, req?.data?.message);
      if (req?.data?.type === "success") {
        queryClient.invalidateQueries({ queryKey: ["myTuitionHistory"] });
      }
      setLoadingVerify(false);
    } catch (error) {
      swal("error", error?.message || "Verification failed");
      setLoadingVerify(false);
    }
  };

  const _resetInvoice = async () => {
    try {
      const confirm = await Swal.fire({
        title: "RESET INVOICE?",
        html: `Are you sure you want to cancel and reset this invoice?<br/><br/>
        <div style="text-align: left; background: #fff1f2; border: 1px solid #fecdd3; padding: 12px; border-radius: 6px; font-size: 13px; color: #9f1239;">
          <strong>Important:</strong><br/>
          • If you have already made payment for this invoice, click <b>Keep Invoice</b> and use <b>Verify</b> instead.<br/>
          • Resetting will safely back up this invoice and allow you to generate a fresh invoice with the current fee schedule.
        </div>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Reset Invoice",
        cancelButtonText: "Keep Invoice",
        confirmButtonColor: "#dc2626",
        cancelButtonColor: "#6b7280",
      });

      if (!confirm.isConfirmed) return;

      setLoadingReset(true);
      const invoiceId = v?.attempted?._id;
      const reference = v?.attempted?.gatewayReference || v?.attempted?.rrr;

      const response = await axiospost("ms_transactions/reset-invoice", {
        id: invoiceId,
        reference,
      });

      setLoadingReset(false);
      const { type, message } = response?.data || {};
      swal(type || "error", message || "Failed to reset invoice");

      if (type === "success") {
        queryClient.invalidateQueries({ queryKey: ["myTuitionHistory"] });
      }
    } catch (error) {
      setLoadingReset(false);
      swal("error", error?.response?.data?.message || error?.message || "Failed to reset invoice");
    }
  };

  const _makePayment = () => {
    const paymentUrl = v?.attempted?.paymentUrl;
    if (paymentUrl) {
      window.open(paymentUrl, "_blank");
    } else {
      swal("error", "Payment URL not available");
    }
  };

  const _openReceipt = () => {
    window.open(`${appHost}payment-receipt/${v?.payment?._id}`, `Student Payment Receipt`, `_parent`, `width=450,height=650`);
  };

  return (
    <>
      <ReactModal isOpen={showmodal} onRequestClose={() => setShowmodal(false)} className="ReactModal">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title text-center">PAYMENT INVOICE</h5>
              <span className="pointer text-danger" onClick={() => setShowmodal(false)}>
                x
              </span>
            </div>
            <div className="modal-body" style={{ minHeight: "70vh" }}>
              {gateway && (
                <div className="alert alert-info mb-3">
                  <i className="fa fa-credit-card"></i> Payment via <strong>{gateway.displayName}</strong> ({gateway.environment} mode)
                </div>
              )}
              {v?.isActive && (
                <>
                  <div className="table-responsive mb-3">
                    <table className="table table-hover table-bordered" id="example">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Description</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {v?.feeItems?.items?.map((item, i) => (
                          <tr key={item._id}>
                            <th>{i + 1}</th>
                            <td>{item.label}</td>
                            <td>{is_money(item.amount)}</td>
                          </tr>
                        ))}
                        {v?.feeItems?.scholar && (
                          <>
                            <tr>
                              <th>{v?.feeItems?.items?.length + 1}</th>
                              <th>Expected Total </th>
                              <th>{is_money(totalAmount)}</th>
                            </tr>
                            <tr>
                              <th>{v?.feeItems?.items?.length + 2}</th>
                              <th>Scholarship</th>
                              <th>{v?.feeItems?.scholar?.percentage}% off</th>
                            </tr>
                          </>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th colSpan={2}> Total</th>
                          <th>{is_money(finalAmount)}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer" style={{ flexDirection: "column", gap: "8px" }}>
              {walletBalance >= finalAmount ? (
                <Button fullWidth variant="success" onClick={_payFromWallet} disabled={loadingWallet}>
                  {loadingWallet
                    ? `Processing...`
                    : `Pay from Wallet (${is_money(walletBalance)})`}
                </Button>
              ) : (
                <Button fullWidth disabled>
                  <i className="fa fa-wallet"></i> Pay from Wallet (Insufficient)
                </Button>
              )}
              <Button fullWidth onClick={_generateInvoice} disabled={loadingInvoice}>
                {loadingInvoice
                  ? `Processing...`
                  : `Pay ${is_money(finalAmount)}${gateway ? ` via ${gateway.displayName}` : ""}`}
              </Button>
            </div>
          </div>
        </div>
      </ReactModal>

      {v?.isAdmitted ? (
        <>
          {v?.payment ? (
            <Button fullWidth variant="success" onClick={_openReceipt}>
              <i className="fa fa-print"></i> Print Receipt
            </Button>
          ) : v?.attempted ? (
            <div className="flex flex-col gap-2 w-full">
              <div className="flex gap-2 w-full">
                {v?.feeItems && walletBalance >= finalAmount ? (
                  <Button fullWidth variant="success" onClick={_payFromWallet} disabled={loadingWallet || loadingReset}>
                    <i className="fa fa-wallet mr-1"></i> {loadingWallet ? `...` : `₦${walletBalance.toLocaleString()}`}
                  </Button>
                ) : v?.feeItems ? (
                  <Button fullWidth disabled>
                    <i className="fa fa-wallet mr-1"></i> Insufficient
                  </Button>
                ) : null}
                {v?.attempted?.paymentUrl && (
                  <Button fullWidth onClick={_makePayment} disabled={loadingReset}>
                    <i className="fa fa-credit-card mr-1"></i> Pay
                  </Button>
                )}
                <Button fullWidth variant="success" onClick={_verifyPayment} disabled={loadingVerify || loadingReset}>
                  <i className={loadingVerify ? "fa fa-spinner fa-spin mr-1" : "fa fa-check-circle mr-1"}></i> Verify
                </Button>
              </div>
              <Button
                fullWidth
                variant="danger"
                size="sm"
                onClick={_resetInvoice}
                disabled={loadingReset || loadingVerify}
              >
                <i className={loadingReset ? "fa fa-spinner fa-spin mr-1" : "fa fa-rotate-left mr-1"}></i>{" "}
                {loadingReset ? "Resetting Invoice..." : "Reset Invoice"}
              </Button>
            </div>
          ) : v?.isActive ? (
            <Button fullWidth onClick={() => setShowmodal(true)}>
              <i className="fa fa-file-invoice-dollar"></i> Generate Invoice
            </Button>
          ) : (
            <Button fullWidth disabled>
              <i className="fa fa-lock"></i> Payment Closed
            </Button>
          )}
        </>
      ) : (
        <Button fullWidth disabled>Not Admitted</Button>
      )}
    </>
  );
};

export default ActionPayment;
